import { useEffect, useMemo, useState } from "react";
import { DocumentDTO } from "../types/DocumentDTO";
import {
  getDocumentMPK,
  getMPKDictionaries,
  type DimensionValue,
  type MPKDictionariesResponse,
  type MPKDimensionDefinition,
  type MPKRowDTO,
} from "../api/mpk";
import {
  getDocumentPurchaseLines,
  getPurchaseLineFeatures,
  type PurchaseLineDTO,
} from "../api/documents";
import {
  getMpkExpectedNetAmount,
  hasRequiredPurchaseLineAmounts,
  isOptionalSettlementLine,
  isOptionalSettlementMpkRow,
  isSettlementDimensionLine,
} from "../utils/settlementMpk";

export type MpkDimensionSelection = {
  dimensionCode: string;
  dimensionValueCode?: string;
};

export type MpkRow = {
  id: string;
  systemId?: string;
  purchaseLineNo?: number;

  amount: number;
  percent: number;
  purchaseCost?: string;
  purchaseCostText?: string;

  /**
   * Nowy model:
   * key = "dimension1"..."dimension10"
   * value = kod wartości wymiaru, np. "ADM", "P0001"
   */
  dimensionValues: Record<string, string | undefined>;

  /**
   * Nowy model payloadu do zapisu.
   */
  dimensions: MpkDimensionSelection[];

  /**
   * Tymczasowa kompatybilność ze starym komponentem DocumentMpkTable.
   * Usuniemy po pełnym przejściu na DocumentMpkV2.
   */
  purchaserCode?: string;
  purchaserName?: string;
  customerGroupCode?: string;
  customerGroupName?: string;
};

function round2(value: number) {
  return Number(value.toFixed(2));
}

function isAlmostEqual(a: number, b: number, precision = 0.01) {
  return Math.abs(a - b) <= precision;
}

function makeLocalId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
}

function getRowField(
  row: MPKRowDTO,
  prefix: "dimensionCode" | "dimensionValueCode",
  order: number
): string | undefined {
  return row[`${prefix}${order}` as keyof MPKRowDTO] as string | undefined;
}

function findDimensionValueName(
  values: DimensionValue[] | undefined,
  code?: string
) {
  if (!code) return undefined;

  return values?.find((value) => value.code === code)?.name;
}

function normalizeExistingRow(
  row: MPKRowDTO,
  activeDimensions: MPKDimensionDefinition[],
  purchaseLines: PurchaseLineDTO[]
): MpkRow {
  const amount = Number(row.amount || 0);
  const purchaseLine = purchaseLines.find(
    (line) => line.lineNo === row.purchaseLineNo
  );
  const purchaseLineAmount = Number(purchaseLine?.amount || 0);

  const percent =
    Math.abs(purchaseLineAmount) > 0.01
      ? round2((amount / purchaseLineAmount) * 100)
      : 0;

  const dimensionValues: Record<string, string | undefined> = {};

  /**
   * Czytamy wszystkie pola dimensionCode1...10 z BC.
   * Dzięki temu nie jesteśmy uzależnieni wyłącznie od pozycji,
   * tylko realnie mapujemy po dimensionCode.
   */
  for (let index = 1; index <= 10; index += 1) {
    const dimensionCode = getRowField(row, "dimensionCode", index);
    const dimensionValueCode = getRowField(row, "dimensionValueCode", index);

    if (!dimensionCode || !dimensionValueCode) {
      continue;
    }

    const matchedDimension = activeDimensions.find(
      (dimension) => dimension.code === dimensionCode
    );

    if (!matchedDimension) {
      continue;
    }

    dimensionValues[matchedDimension.key] = dimensionValueCode;
  }

  const dimensions: MpkDimensionSelection[] = activeDimensions
    .map((dimension) => {
      const dimensionValueCode = dimensionValues[dimension.key];

      if (!dimensionValueCode) {
        return null;
      }

      return {
        dimensionCode: dimension.code,
        dimensionValueCode,
      };
    })
    .filter(Boolean) as MpkDimensionSelection[];

  const dimension1 = activeDimensions.find((dimension) => dimension.order === 1);
  const dimension2 = activeDimensions.find((dimension) => dimension.order === 2);

  const dimension1ValueCode = dimension1
    ? dimensionValues[dimension1.key]
    : undefined;

  const dimension2ValueCode = dimension2
    ? dimensionValues[dimension2.key]
    : undefined;

  return {
    id: row.systemId || makeLocalId(),
    systemId: row.systemId,
    purchaseLineNo: row.purchaseLineNo,

    amount,
    percent,
    purchaseCost: row.purchaseCost,
    purchaseCostText: row.purchaseCostText,

    dimensionValues,
    dimensions,

    purchaserCode: dimension1ValueCode,
    purchaserName: findDimensionValueName(dimension1?.values, dimension1ValueCode),

    customerGroupCode: dimension2ValueCode,
    customerGroupName: findDimensionValueName(dimension2?.values, dimension2ValueCode),
  };
}

function buildDuplicateKey(
  row: MpkRow,
  activeDimensions: MPKDimensionDefinition[]
) {
  return activeDimensions
    .map((dimension) => row.dimensionValues[dimension.key] || "")
    .join("|");
}

export function useMpkDimensions(
  document: DocumentDTO,
  companyId: string,
  refreshKey = 0
) {
  const [rows, setRows] = useState<MpkRow[]>([]);
  const [dictionaries, setDictionaries] =
    useState<MPKDictionariesResponse | null>(null);
  const [purchaseLines, setPurchaseLines] = useState<PurchaseLineDTO[]>([]);
  const [settlementDimensionsOptional, setSettlementDimensionsOptional] = useState(false);
  const [requireSettlementDimensions, setRequireSettlementDimensions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentNetAmount = Number(document.amounts?.net || 0);

  useEffect(() => {
    if (!companyId || !document.id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const [dictionariesData, mpkRows, purchaseLineRows, features] = await Promise.all([
          getMPKDictionaries(companyId),
          getDocumentMPK(companyId, document.id),
          getDocumentPurchaseLines(companyId, document.id),
          getPurchaseLineFeatures(companyId),
        ]);

        if (cancelled) return;

        const activeDimensions = dictionariesData.dimensions ?? [];

        setDictionaries(dictionariesData);
        setPurchaseLines(purchaseLineRows);
        setSettlementDimensionsOptional(
          features.useKSeFSettlementHandling && !features.requireSettlementDimensions
        );
        setRequireSettlementDimensions(
          features.useKSeFSettlementHandling && features.requireSettlementDimensions
        );
        setRows(
          mpkRows.map((row) =>
            normalizeExistingRow(row, activeDimensions, purchaseLineRows)
          )
        );
      } catch (e) {
        console.error(e);

        if (!cancelled) {
          setError("Nie udało się pobrać MPK");
          setRows([]);
          setPurchaseLines([]);
          setSettlementDimensionsOptional(false);
          setRequireSettlementDimensions(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [companyId, document.id, documentNetAmount, refreshKey]);

  const activeDimensions = useMemo(() => {
    return dictionaries?.dimensions ?? [];
  }, [dictionaries]);

  const hasAnyDimension =
    Boolean(dictionaries?.useMPKDimension) && activeDimensions.length > 0;

  /**
   * Tymczasowa kompatybilność ze starymi warunkami.
   */
  const hasDimension1 = activeDimensions.some(
    (dimension) => dimension.order === 1
  );

  const hasDimension2 = activeDimensions.some(
    (dimension) => dimension.order === 2
  );

  const dimensionHeaders = activeDimensions.map((dimension) => ({
    key: dimension.key,
    code: dimension.code,
    label: dimension.name || dimension.code,
    order: dimension.order,
    source: dimension.source,
  }));

  const requiredNetAmount = getMpkExpectedNetAmount(
    documentNetAmount, purchaseLines, settlementDimensionsOptional
  );

  const totalAmount = useMemo(() => {
    return round2(
      rows.filter((row) => !isOptionalSettlementMpkRow(
        row.purchaseLineNo, purchaseLines, settlementDimensionsOptional
      )).reduce((sum, row) => sum + Number(row.amount || 0), 0)
    );
  }, [rows, purchaseLines, settlementDimensionsOptional]);

  const totalPercent = useMemo(() => {
    return round2(
      rows.reduce((sum, row) => sum + Number(row.percent || 0), 0)
    );
  }, [rows]);

  const remainingAmount = round2(requiredNetAmount - totalAmount);

  const hasDuplicateRows = useMemo(() => {
    if (!hasAnyDimension || rows.length <= 1) {
      return false;
    }

    const keys = rows.map((row) =>
      `${row.purchaseLineNo ?? ""}|${buildDuplicateKey(row, activeDimensions)}`
    );

    return new Set(keys).size !== keys.length;
  }, [rows, activeDimensions, hasAnyDimension]);

  const lineAllocationValidation = useMemo(() => {
    const amountByLine = new Map<number, number>();
    const percentByLine = new Map<number, number>();

    rows.forEach((row) => {
      if (row.purchaseLineNo === undefined) return;
      amountByLine.set(
        row.purchaseLineNo,
        round2((amountByLine.get(row.purchaseLineNo) ?? 0) + Number(row.amount || 0))
      );
      percentByLine.set(
        row.purchaseLineNo,
        round2((percentByLine.get(row.purchaseLineNo) ?? 0) + Number(row.percent || 0))
      );
    });

    const relevantLines = purchaseLines.filter(
      (line): line is PurchaseLineDTO & { lineNo: number } =>
        line.lineNo !== null && Math.abs(Number(line.amount || 0)) > 0.01 &&
        !isOptionalSettlementLine(line, settlementDimensionsOptional)
    );

    const everyLineComplete = relevantLines.every((line) => {
      const target = Number(line.amount || 0);
      return (
        isAlmostEqual(amountByLine.get(line.lineNo) ?? 0, target) &&
        isAlmostEqual(percentByLine.get(line.lineNo) ?? 0, 100)
      );
    });

    return {
      everyLineComplete,
      hasMissingPurchaseLine: rows.some((row) => row.purchaseLineNo === undefined),
    };
  }, [rows, purchaseLines, settlementDimensionsOptional]);

  const isAmountValid = isAlmostEqual(totalAmount, requiredNetAmount);
  const isPercentValid = lineAllocationValidation.everyLineComplete;

  const hasRequiredLines = hasRequiredPurchaseLineAmounts(
    purchaseLines, settlementDimensionsOptional
  );
  const settlementDimensionsComplete = !requireSettlementDimensions ||
    rows.every((row) => {
      const line = purchaseLines.find((item) => item.lineNo === row.purchaseLineNo);
      if (!line || !isSettlementDimensionLine(line)) {
        return true;
      }
      return activeDimensions.every((dimension) =>
        Boolean(row.dimensionValues[dimension.key]?.trim())
      );
    });

  const isMPKValid =
    !hasAnyDimension ||
    (
      (rows.length > 0 || (settlementDimensionsOptional && !hasRequiredLines && purchaseLines.length > 0)) &&
      isAmountValid &&
      isPercentValid &&
      settlementDimensionsComplete &&
      !lineAllocationValidation.hasMissingPurchaseLine &&
      !hasDuplicateRows
    );

  const editable = document.status?.code !== "Posted" && document.status?.code !== "Approved";

  return {
    rows,
    setRows,

    dictionaries,
    activeDimensions,
    purchaseLines,
    settlementDimensionsOptional,
    requiredNetAmount,

    loading,
    error,

    editable,

    hasAnyDimension,
    hasDimension1,
    hasDimension2,

    dimensionHeaders,

    totalAmount,
    totalPercent,
    remainingAmount,

    hasDuplicateRows,
    hasMissingPurchaseLine: lineAllocationValidation.hasMissingPurchaseLine,
    isAmountValid,
    isPercentValid,
    isMPKValid,
  };
}